/**
 * Quick Sale Types
 *
 * Quick Sales allow staff to sell products directly to clients without creating an appointment.
 */

import {
  ApplyPresetLineItemDiscountInput,
  LineDiscountEntry,
  LineItemDiscount,
} from "./discounts";

// Product in a quick sale request
export interface QuickSaleProduct {
  product_uuid: string;
  quantity: number;
  /** Presets to apply to the line (stacked discounts). */
  discounts?: ApplyPresetLineItemDiscountInput[];
  staff_commission_target?: string | null;
}

// Request payload for creating a quick sale
export interface CreateQuickSaleRequest {
  clientId: string;
  products: QuickSaleProduct[];
}

// Line item in the response
export interface QuickSaleLineItem {
  id: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  /** Discounts applied to the line (stacked). */
  discounts?: LineItemDiscount[];
  /** Dollars discounted off the line; finalAmount = lineTotal − discountTotal. */
  discountTotal?: number;
  finalAmount: number;
}

// Successful response from create-quick-sale
export interface CreateQuickSaleResponse {
  transactionId: string;
  paymentIntentId: string;
  eligibleAmountForTip: number; // Always 0 for quick sales
  totalAmount: number;
  lineItems: QuickSaleLineItem[];
}

// Partial failure response (transaction created but payment intent failed)
export interface CreateQuickSalePartialFailureResponse {
  message: string;
  transactionId: string;
  transactionCreated: true;
}

// Type guard to check if response is a partial failure
export function isQuickSalePartialFailure(
  response: CreateQuickSaleResponse | CreateQuickSalePartialFailureResponse,
): response is CreateQuickSalePartialFailureResponse {
  return (
    "transactionCreated" in response && response.transactionCreated === true
  );
}

// In-memory product state for the QuickSaleModal
export interface QuickSaleProductState {
  productId: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  imgUrl?: string;
  availableStock: number;
  /**
   * Stacked discounts on the cart line. Entries keep their applied-discount
   * `id` once persisted; staged ones carry only the preset.
   */
  discounts: LineDiscountEntry[];
  staffCommissionTarget?: string | null;
}

// Helper to check if a transaction is a quick sale
// Quick Sales are identified by having NO booked services (appointment_status can be anything)
export function isQuickSaleTransaction(transaction: {
  line_items: { booked_services?: any[] };
}): boolean {
  return (
    !transaction.line_items.booked_services ||
    transaction.line_items.booked_services.length === 0
  );
}
